/**
 * @fileoverview useCategoryBalances - Per-category balance tracking hook.
 *
 * Fetches and manages balance information broken down by category for a selected
 * month. Shows starting balance, credits (income), debits (spending), and ending
 * balance for each budget category.
 *
 * @description This hook provides:
 * - Category-level balance tracking
 * - Automatic refresh when month changes
 * - Easy balance lookup by category name
 * - Budget vs actual spending comparison
 * - Automatic budget context subscription
 *
 * Category balance structure:
 * {
 *   "Groceries": {
 *     startingBalance: 100,
 *     credits: 500,
 *     debits: 450,
 *     endingBalance: 150
 *   },
 *   ...
 * }
 *
 * Use cases:
 * - Show spending progress per category
 * - Budget vs actual comparison
 * - Category overspending alerts
 *
 * @module hooks/useCategoryBalances
 */
import { useState, useEffect, useCallback } from 'react';
import { monthAPI, onBudgetContextChange } from '../services/api';
import { useRealtime } from '../contexts/RealtimeContext';

/**
 * Hook for managing category balance data.
 *
 * @param {Date|string} selectedMonth - The month as a Date object or YYYY-MM string
 * @returns {Object} Category balance data and operations
 * @returns {Object} return.categoryBalances - Object keyed by category name
 * @returns {Error|null} return.error - Any fetch error
 * @returns {boolean} return.isLoading - Whether balances are loading
 * @returns {Function} return.refreshCategoryBalances - Manually refresh balances
 * @returns {Function} return.getBalanceForCategory - Get balance for specific category
 *
 * @example
 * const catBalances = useCategoryBalances(selectedMonth);
 * const groceries = catBalances.getBalanceForCategory('Groceries');
 * console.log(`Spent: ${groceries.debits}`);
 */
export function useCategoryBalances(selectedMonth) {
    const [categoryBalances, setCategoryBalances] = useState({});
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Realtime context for live balance updates
    const { subscribeToEvent, isConnected } = useRealtime();

    // Convert Date object to YYYY-MM format
    const getMonthKey = useCallback((date) => {
        if (!date) return null;

        if (typeof date === 'string') {
            // If already a string, extract YYYY-MM
            const [year, month] = date.split('-');
            return `${year}-${month}`;
        }

        if (date instanceof Date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            return `${year}-${month}`;
        }

        return null;
    }, []);

    // Fetch category balances from API
    const fetchCategoryBalances = useCallback(async () => {
        const monthKey = getMonthKey(selectedMonth);
        if (!monthKey) return;

        // Wait for budget context to be set before loading
        const { transactionAPI } = require('../services/api');
        if (!transactionAPI.getCurrentBudgetId()) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const data = await monthAPI.getCategoryBalances(monthKey);

            // Convert array to object keyed by category name for easy lookup
            const balancesMap = {};
            data.forEach(categoryBalance => {
                balancesMap[categoryBalance.categoryName] = {
                    startingBalance: categoryBalance.startingBalance || 0,
                    credits: categoryBalance.credits || 0,
                    debits: categoryBalance.debits || 0,
                    endingBalance: categoryBalance.endingBalance || 0
                };
            });

            setCategoryBalances(balancesMap);
        } catch (err) {
            // Error handling
            setError('Error updating category balances');
            // Keep the old balance data on error
        } finally {
            setIsLoading(false);
        }
    }, [selectedMonth, getMonthKey]);

    // Fetch balances when month changes
    useEffect(() => {
        fetchCategoryBalances();

        // Subscribe to budget context changes and reload when it's set
        const unsubscribe = onBudgetContextChange((budgetId) => {
            if (budgetId) {
                fetchCategoryBalances();
            }
        });

        return unsubscribe;
    }, [fetchCategoryBalances]);

    // Subscribe to realtime balance updates
    useEffect(() => {
        if (!subscribeToEvent) return;

        const unsubscribe = subscribeToEvent('balance:updated', (data) => {
            // Refresh category balances when notified of changes
            const currentMonthKey = getMonthKey(selectedMonth);
            if (data.monthKey === currentMonthKey || !data.monthKey) {
                fetchCategoryBalances();
            }
        });

        return unsubscribe;
    // isConnected in deps ensures we re-subscribe when socket connects
    }, [subscribeToEvent, selectedMonth, getMonthKey, fetchCategoryBalances, isConnected]);

    // Helper to get balance for a specific category
    const getBalanceForCategory = useCallback((categoryName) => {
        return categoryBalances[categoryName] || {
            startingBalance: 0,
            credits: 0,
            debits: 0,
            endingBalance: 0
        };
    }, [categoryBalances]);

    return {
        categoryBalances,
        error,
        isLoading,
        refreshCategoryBalances: fetchCategoryBalances,
        getBalanceForCategory
    };
}
